/* Gleva · Direction B · gvb-studio.js — the studio backdrop for white-background product photos.
   Loaded once from layout/theme.liquid (classic script, async). Styles: assets/gvb-studio.css.

   Markup (the Liquid snippets decide which frames take part):
     <a class="pcard-media sb" data-studio="auto"> <img class="sb-img" …> </a>   auto: classified here ("" works too)
     <a class="pcard-media sb sb-on sb-done sb-instant" data-studio="studio">   forced (metafield gvb.card_style = studio)
     <a class="pcard-media" data-studio="off">                                  never (curated gvb.card_image, card_style =
       photo, setting off); "off" also keeps gvb-collection.js's older corner sampler away from the frame.
   gvb_slide gallery blocks carry no data-studio at all and are never touched.
   After classification data-studio reads "studio", "cut-<sides>" (e.g. "cut-b", "cut-tb") or "photo".

   Classification: the frame's .sb-img (or first <img>) is drawn on a small same-origin canvas (64px wide) and its outer
   ring (2px ≈ 3%) is read. A pixel is "near-white" when its smallest channel is ≥ 236 (or it is transparent).
     - ≥ 85% of the ring near-white and no side touched                       → studio
     - one or two sides touched (< 94% white) and the other sides untouched → studio, anchored to the touched side(s)
       ("untouched" = ≥ 94% white; with a single touched side ≥ 90% is enough, e.g. a sponge jar or a night wrap whose
       shadow grazes a second edge), and only when ≥ 60% of the whole ring is near-white (a floor or wall photo that
       happens to be bright along one edge is not a packshot)
     - every side ≥ 85% white and the ring ≥ 88% (a packshot whose products brush several edges: sponge jar, night
       wrap, bonnet)                                                           → studio, uncut
       (.sb-cut-b, .sb-cut-t + .sb-cut-b, .sb-cut-t + .sb-cut-r …): the product runs off that edge in the photo, so it
       meets the frame edge there with no room and the cut reads as a crop (a tall bottle cut top and bottom fills the
       height; a product cut at a corner sits in that corner). Three or four touched sides → photo.
     - a tinted near-white ground (mint, lilac, pink, sage infographics whose channels all stay ≥ 236): the mean colour of
       the near-white ring has a CIE Lab chroma > 3.5, or a channel spread (max − min) > 12 → photo. Multiplying such a
       ground onto the warm backdrop would leave a muddy khaki / olive box (PLAN A4). Warm off-white studio grounds
       (e.g. 255,249,246: chroma ≈ 2.8) stay studio and are white-balanced as below.
     - anything else (lifestyle, coloured backgrounds, infographics with full-bleed colour)  → photo, left exactly as is
   Photo frames also get the photo's shape, so the CSS can pick fill vs. whole-photo-on-the-mat per frame (PLAN A5/A6):
     .sb-photo-sq (aspect ≥ 0.95: square or wider, AND a flat ring — ≥ 60% of the outer ring within a small distance of
     its mean colour, i.e. a designed tile with a plain ground, where baked-in text runs to the sides; a 4:5 card shows
     it whole on the cream mat instead of side-cropping that text. A full-bleed lifestyle photo has a busy ring and
     keeps filling the card) and data-ar = "t" (< 0.9, portrait) | "s" (0.9–1.15, square) | "w" (> 1.15, landscape).
   A canvas that cannot be read (a cross-origin image without CORS) leaves the photo as is.
   An off-white ground (min channel < 252) is white-balanced to pure white (SVG feComponentTransfer, --sb-wb on the
   frame), so it disappears into the backdrop instead of leaving a faint tinted box.
   The frame is cream from the first paint; the image fades in (120 ms, none with reduced motion) once classified.
   Results are cached per image path (memory + sessionStorage), so the next page applies them before the image arrives.

   API (also for the cart lane):
     GVBStudio.classify(img)  → Promise<{ studio: bool, cut: null | one or two of 't','r','b','l' ('b', 'tb', 'tr'…), bg: [r,g,b]|null, white: 0–1, sides: {t,r,b,l}, tint: {chroma, spread}|null, ar: w/h, flat: 0–1, cached?, error? }>
     GVBStudio.filter(bg)     → 'url(#sb-wb-…)' white-balance filter for an off-white ground ('' for none)
     GVBStudio.apply(frame)   → Promise<result>  classifies frame's image and sets .sb-on / .sb-cut-* / .sb-done
     GVBStudio.scan(root)     → applies every auto frame under root (new markup is picked up automatically) */
(function () {
  'use strict';
  if (window.GVBStudio) return;

  var WHITE = 236;          /* min channel for "near-white" */
  var RING_MIN = 0.85;      /* share of the ring that must be near-white */
  var TOUCH = 0.94;         /* a side below this is touched by the product */
  var CLEAN = 0.94;         /* the other sides must be at least this white for an edge-cut (= untouched) */
  var CLEAN_1 = 0.90;       /* … relaxed when only one side is touched */
  var CUT_RING = 0.6;       /* an edge-cut also needs this share of the whole ring near-white */
  var SOFT_SIDE = 0.85;     /* "grazed": every side at least this white … */
  var SOFT_RING = 0.88;     /* … and the ring at least this white → studio, uncut (products that brush the edges) */
  var CHROMA = 3.5;         /* Lab chroma of the ring's near-white mean above this = a tinted ground → photo */
  var SPREAD = 12;          /* … or a channel spread (max − min) above this */
  var FLAT = 0.6;           /* a photo whose ring is at least this flat is a designed tile (see .sb-photo-sq) */
  var W = 64;               /* canvas width */
  var DEPTH = 2;            /* ring depth in canvas px */
  var STORE = 'gvb-sb6';

  var mem = {};
  var store = {};
  try { store = JSON.parse(window.sessionStorage.getItem(STORE) || '{}') || {}; } catch (e) { store = {}; }
  var saveT = 0;
  var save = function () {
    if (saveT) return;
    saveT = setTimeout(function () {
      saveT = 0;
      try {
        var keys = Object.keys(store);
        if (keys.length > 600) keys.slice(0, keys.length - 600).forEach(function (k) { delete store[k]; });
        window.sessionStorage.setItem(STORE, JSON.stringify(store));
      } catch (e) { /* storage blocked: memory cache only */ }
    }, 400);
  };

  /* one key per photo, whatever width Shopify serves: the path without the query */
  var keyOf = function (src) {
    if (!src) return '';
    try { return new URL(src, location.href).pathname; } catch (e) { return String(src).split('?')[0]; }
  };
  var pack = function (r) { return (r.studio ? 's' + (r.cut || '') : 'p') + '|' + (r.studio && r.bg ? r.bg.join(',') : '') + '|' + (r.ar || '') + '|' + (r.flat == null ? '' : r.flat); };
  var unpack = function (c) {
    var parts = c.split('|');
    return { studio: c.charAt(0) === 's', cut: parts[0].length > 1 ? parts[0].slice(1) : null, bg: parts[1] ? parts[1].split(',').map(Number) : null, ar: parts[2] ? Number(parts[2]) : null, flat: parts[3] ? Number(parts[3]) : null, cached: true };
  };
  /* CIE Lab chroma of an sRGB colour (D65) */
  var chroma = function (c) {
    var lin = function (v) { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    var r = lin(c[0]), g = lin(c[1]), b = lin(c[2]);
    var x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047, y = r * 0.2126 + g * 0.7152 + b * 0.0722, z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
    var f = function (t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; };
    var A = 500 * (f(x) - f(y)), B = 200 * (f(y) - f(z));
    return Math.sqrt(A * A + B * B);
  };

  /* per-channel gain that maps the photo's off-white ground to pure white (feComponentTransfer), one filter per colour */
  var defs = null;
  var wb = function (c) {
    if (!c) return '';
    var id = 'sb-wb-' + c.join('-');
    if (!document.getElementById(id)) {
      if (!defs) {
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('width', '0');
        svg.setAttribute('height', '0');
        svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svg.appendChild(defs);
        (document.body || document.documentElement).appendChild(svg);
      }
      var f = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
      f.setAttribute('id', id);
      f.setAttribute('color-interpolation-filters', 'sRGB');
      var ct = document.createElementNS('http://www.w3.org/2000/svg', 'feComponentTransfer');
      ['R', 'G', 'B'].forEach(function (ch, i) {
        var fn = document.createElementNS('http://www.w3.org/2000/svg', 'feFunc' + ch);
        fn.setAttribute('type', 'linear');
        fn.setAttribute('slope', (255 / Math.max(200, c[i])).toFixed(4));
        ct.appendChild(fn);
      });
      f.appendChild(ct);
      defs.appendChild(f);
    }
    return 'url(#' + id + ')';
  };

  var canvas = null;
  var ctx = null;
  var analyse = function (img) {
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) throw new Error('not decoded');
    var h = Math.max(16, Math.min(160, Math.round(W * nh / nw)));
    if (!canvas) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    canvas.width = W;
    canvas.height = h;
    ctx.clearRect(0, 0, W, h);
    ctx.drawImage(img, 0, 0, W, h);
    var d = ctx.getImageData(0, 0, W, h).data; /* throws SecurityError on a tainted canvas */
    var white = function (x, y) {
      var i = (y * W + x) * 4;
      return d[i + 3] < 16 || (d[i] >= WHITE && d[i + 1] >= WHITE && d[i + 2] >= WHITE);
    };
    var side = function (x0, y0, x1, y1) {
      var n = 0, w = 0;
      for (var y = y0; y < y1; y++) for (var x = x0; x < x1; x++) { n++; if (white(x, y)) w++; }
      return { n: n, w: w };
    };
    /* the background's own colour (mean of the near-white ring pixels): an off-white ground (255,249,246) would
       multiply into a faint tinted box, so it is white-balanced to pure white with a tiny SVG filter (paint) */
    var bg = [0, 0, 0], bn = 0;
    var tally = function (x, y) { var i = (y * W + x) * 4; if (d[i + 3] >= 16 && white(x, y)) { bg[0] += d[i]; bg[1] += d[i + 1]; bg[2] += d[i + 2]; bn++; } };
    for (var xx = 0; xx < W; xx++) { tally(xx, 0); tally(xx, h - 1); }
    for (var yy = 1; yy < h - 1; yy++) { tally(0, yy); tally(W - 1, yy); }
    /* how flat the ring is: the share of ring pixels close (max channel difference ≤ 28) to the ring's mean colour.
       A designed tile (infographic, text card, coloured ground) has a flat ring; a lifestyle photo does not. */
    var all = [0, 0, 0], an = 0, ringPx = [];
    var grab = function (x, y) { var i = (y * W + x) * 4; ringPx.push(i); all[0] += d[i]; all[1] += d[i + 1]; all[2] += d[i + 2]; an++; };
    for (var x2 = 0; x2 < W; x2++) { grab(x2, 0); grab(x2, h - 1); }
    for (var y2 = 1; y2 < h - 1; y2++) { grab(0, y2); grab(W - 1, y2); }
    var mean = all.map(function (v) { return v / an; });
    var near = 0;
    ringPx.forEach(function (i) { if (Math.max(Math.abs(d[i] - mean[0]), Math.abs(d[i + 1] - mean[1]), Math.abs(d[i + 2] - mean[2])) <= 28) near++; });
    var flat = Math.round(near / an * 1000) / 1000;
    var t = side(0, 0, W, DEPTH);
    var b = side(0, h - DEPTH, W, h);
    var l = side(0, DEPTH, DEPTH, h - DEPTH);
    var r = side(W - DEPTH, DEPTH, W, h - DEPTH);
    var sides = { t: t.w / t.n, r: r.w / r.n, b: b.w / b.n, l: l.w / l.n };
    var ring = (t.w + b.w + l.w + r.w) / (t.n + b.n + l.n + r.n);
    var touched = ['t', 'r', 'b', 'l'].filter(function (k) { return sides[k] < TOUCH; });
    var res = { studio: false, cut: null, white: Math.round(ring * 1000) / 1000, sides: sides, bg: null, tint: null, ar: Math.round(nw / nh * 1000) / 1000, flat: flat };
    var tinted = false;
    if (bn) {
      var c = bg.map(function (v) { return Math.round(v / bn); });
      if (Math.min(c[0], c[1], c[2]) < 252) res.bg = c;
      res.tint = { chroma: Math.round(chroma(c) * 10) / 10, spread: Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]) };
      tinted = res.tint.chroma > CHROMA || res.tint.spread > SPREAD;
    }
    if (tinted) return res;
    if (touched.length === 0 && ring >= RING_MIN) res.studio = true;
    else if (touched.length <= 2 && ring >= CUT_RING) {
      var clean = touched.length === 1 ? CLEAN_1 : CLEAN;
      var ok = ['t', 'r', 'b', 'l'].every(function (k) { return touched.indexOf(k) >= 0 || sides[k] >= clean; });
      if (ok) { res.studio = true; res.cut = touched.join(''); }
    }
    /* not an edge-cut, but every side only grazed: a packshot whose products brush several edges */
    if (!res.studio && ring >= SOFT_RING && ['t', 'r', 'b', 'l'].every(function (k) { return sides[k] >= SOFT_SIDE; })) res.studio = true;
    return res;
  };

  var loaded = function (img) {
    return new Promise(function (resolve, reject) {
      var go = function () {
        if (!img.naturalWidth) { reject(new Error('no image')); return; }
        if (img.decode) img.decode().then(resolve, resolve); else resolve();
      };
      if (img.complete) { go(); return; }
      var done = function (e) {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        if (e.type === 'error') reject(new Error('load error')); else go();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  };

  var classify = function (img) {
    if (!img) return Promise.resolve({ studio: false, cut: null, error: 'no image' });
    var k0 = keyOf(img.currentSrc || img.src);
    if (k0 && mem[k0]) return Promise.resolve(mem[k0]);
    return loaded(img).then(function () {
      var k = keyOf(img.currentSrc || img.src);
      if (k && mem[k]) return mem[k];
      var res;
      try { res = analyse(img); } catch (e) { return { studio: false, cut: null, error: e && e.name === 'SecurityError' ? 'cors' : String(e && e.message || e) }; }
      if (k) { mem[k] = res; store[k] = pack(res); save(); }
      return res;
    }, function (e) { return { studio: false, cut: null, error: String(e && e.message || e) }; });
  };

  var CUTS = ['sb-cut-t', 'sb-cut-r', 'sb-cut-b', 'sb-cut-l'];
  var paint = function (frame, res, instant) {
    frame.classList.remove.apply(frame.classList, CUTS);
    frame.classList.toggle('sb-on', !!res.studio);
    /* a photo's shape (PLAN A5/A6): the CSS fills the frame or shows the whole photo on the mat */
    var ar = !res.studio && res.ar ? res.ar : 0;
    frame.classList.toggle('sb-photo-sq', ar >= 0.95 && res.flat >= FLAT);
    if (ar) frame.setAttribute('data-ar', ar < 0.9 ? 't' : (ar > 1.15 ? 'w' : 's')); else frame.removeAttribute('data-ar');
    if (res.studio && res.cut) res.cut.split('').forEach(function (k) { frame.classList.add('sb-cut-' + k); });
    if (res.studio && res.bg) frame.style.setProperty('--sb-wb', wb(res.bg)); else frame.style.removeProperty('--sb-wb');
    if (instant) frame.classList.add('sb-instant');
    frame.classList.add('sb-done');
    frame.setAttribute('data-studio', res.studio ? (res.cut ? 'cut-' + res.cut : 'studio') : 'photo');
  };

  var imgOf = function (frame) { return frame.querySelector('img.sb-img') || frame.querySelector('img'); };

  var apply = function (frame) {
    var img = imgOf(frame);
    if (!img) { paint(frame, { studio: false }, true); return Promise.resolve({ studio: false, error: 'no image' }); }
    /* no source yet (the search overlay's idle rows carry data-gvb-src until the first open): wait for the real photo
       instead of classifying an empty image as "photo" */
    if (!img.getAttribute('src') && !img.getAttribute('srcset')) {
      return new Promise(function (resolve) {
        var go = function (e) {
          img.removeEventListener('load', go);
          img.removeEventListener('error', go);
          if (e.type === 'error') { paint(frame, { studio: false }, true); resolve({ studio: false, error: 'load error' }); return; }
          apply(frame).then(resolve);
        };
        img.addEventListener('load', go);
        img.addEventListener('error', go);
      });
    }
    var src = img.currentSrc || img.src || '';
    /* srcset not resolved yet: use the src attribute's path (same file, another width) */
    var k = keyOf(src || img.getAttribute('src'));
    if (k && store[k] && !mem[k]) {
      var r0 = unpack(store[k]);
      paint(frame, r0, true);
      return Promise.resolve(r0);
    }
    /* known photo (a re-rendered bag line, a second rail with the same product): no fade, it simply appears */
    if (k && mem[k]) { paint(frame, mem[k], true); return Promise.resolve(mem[k]); }
    return classify(img).then(function (res) { paint(frame, res, false); return res; });
  };

  /* a frame is taken once its image exists (while the page is still parsing, the frame arrives before its <img>) */
  var AUTO = '[data-studio=""],[data-studio="auto"]';
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
  var take = function (frame) {
    if (!frame || frame.classList.contains('sb-done')) return;
    if (!imgOf(frame)) return;
    if (seen) { if (seen.has(frame)) return; seen.add(frame); } else { if (frame._gvbSb) return; frame._gvbSb = 1; }
    apply(frame);
  };
  var scan = function (root) {
    root = root || document;
    if (root.nodeType === 1 && root.matches(AUTO)) take(root);
    if (root.querySelectorAll) Array.prototype.forEach.call(root.querySelectorAll(AUTO), take);
  };

  var mo = new MutationObserver(function (list) {
    for (var i = 0; i < list.length; i++) {
      var a = list[i].addedNodes;
      for (var j = 0; j < a.length; j++) {
        var n = a[j];
        if (n.nodeType !== 1) continue;
        if (n.tagName === 'IMG') { take(n.closest(AUTO)); continue; }
        if (n.matches(AUTO)) take(n);
        else if (n.firstElementChild && n.querySelector(AUTO)) scan(n);
      }
    }
  });
  window.GVBStudio = { classify: classify, apply: apply, scan: scan, filter: wb, version: 5 };
  try {
    mo.observe(document.documentElement, { childList: true, subtree: true });
    scan(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } catch (e) {
    /* never leave photos hidden: without the script's classes the frames show their photos as uploaded */
    document.documentElement.classList.remove('sb-js');
  }
})();
